import { messages } from '@/lib/i18n/messages'

function defaultMessage(status: number): string {
  return (messages.errors.byStatus as Record<number, string>)[status] ?? messages.errors.unknown
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly errors: Record<string, string[]>,
    public readonly nonFieldErrors: string[],
    /**
     * Raw response body, preserved verbatim so callers can read non-envelope
     * keys the canonical `{errors: ...}` shape doesn't carry. Phase 3 BOM
     * cycle responses surface `cycle_path: [code, ...]` as a top-level key
     * (see `apps/common/exception_handlers.py::extract_cycle_response`);
     * `extractCyclePath` (`features/bom/queries/nomenclature-mutations.ts`)
     * reads it from here.
     */
    public readonly body: unknown = null,
  ) {
    const fallback = nonFieldErrors[0] ?? Object.values(errors)[0]?.[0] ?? defaultMessage(status)
    super(fallback)
    this.name = 'ApiError'
  }
}

export function parseApiError(response: Response, body: unknown): ApiError {
  if (typeof body === 'object' && body !== null) {
    // Canonical envelope: { errors: { field: [...], non_field_errors: [...] } }
    if (
      'errors' in body &&
      typeof (body as { errors: unknown }).errors === 'object' &&
      (body as { errors: unknown }).errors !== null
    ) {
      const errs = (body as { errors: Record<string, string[]> }).errors
      const { non_field_errors = [], ...fieldErrors } = errs
      return new ApiError(response.status, fieldErrors, non_field_errors, body)
    }
    // Ninja default for raised HttpError / unhandled exceptions: { detail: "msg" }.
    // Surface the message verbatim instead of falling back to a generic status copy.
    if ('detail' in body && typeof (body as { detail: unknown }).detail === 'string') {
      const detail = (body as { detail: string }).detail
      if (detail.length > 0) return new ApiError(response.status, {}, [detail], body)
    }
    // DRF-style { message: "msg" } — same idea, kept for resilience.
    if ('message' in body && typeof (body as { message: unknown }).message === 'string') {
      const msg = (body as { message: string }).message
      if (msg.length > 0) return new ApiError(response.status, {}, [msg], body)
    }
  }
  return new ApiError(response.status, {}, [defaultMessage(response.status)], body)
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

/**
 * What the shop calls a field the backend reports by key. `formatApiError`
 * never shows a raw key: a field missing here prints its message alone.
 */
const FIELD_LABELS: Record<string, string> = {
  lines: 'Позиції',
  status: 'Статус',
  code: 'Код',
  name: 'Назва',
  notes: 'Примітка',
  reason: 'Причина',
  quantity: 'Кількість',
  qty: 'Кількість',
  qty_issued: 'Кількість',
  quantity_planned: 'Кількість (план)',
  actual_quantity: 'Кількість (факт)',
  produced_qty: 'Готових',
  length_mm: 'Довжина',
  kerf_mm: 'Пропил',
  project: 'Проєкт',
  project_id: 'Проєкт',
  work_order: 'Наряд',
  work_order_id: 'Наряд',
  work_order_type: 'Тип наряду',
  work_order_types: 'Типи нарядів',
  work_date: 'Дата',
  issue_date: 'Дата',
  receipt_date: 'Дата',
  shift_date: 'Дата',
  transfer_date: 'Дата',
  warehouse: 'Склад',
  warehouse_id: 'Склад',
  source_warehouse: 'Склад-джерело',
  source_warehouse_id: 'Склад-джерело',
  destination_warehouse: 'Склад-отримувач',
  destination_warehouse_id: 'Склад-отримувач',
  recipient: 'Одержувач',
  recipient_id: 'Одержувач',
  storekeeper: 'Комірник',
  storekeeper_id: 'Комірник',
  material: 'Матеріал',
  material_id: 'Матеріал',
  material_stock_lot: 'Матеріал',
  material_stock_item: 'Обрізок',
  stock_item: 'Деталь / вузол',
  part: 'Деталь',
  part_id: 'Деталь',
  assembly: 'Вузол',
  assembly_id: 'Вузол',
  parent_assembly_id: 'Вузол',
  component_id: 'Компонент',
  operation: 'Операція',
  operation_id: 'Операція',
  position_id: 'Посада',
  tab_no: 'Табельний номер',
  email: 'Email',
  substitution_reason: 'Причина заміни',
  deviation_reason: 'Причина відхилення',
}

/** «lines.2.qty_issued» → «Позиція 3 · Кількість»; an unknown key → null. */
function fieldLabel(key: string): string | null {
  const row = /^lines\.(\d+)\.(.+)$/.exec(key)
  if (row) {
    const [, index, field] = row
    const inner = field ? FIELD_LABELS[field] : undefined
    const pos = `Позиція ${Number(index) + 1}`
    return inner ? `${pos} · ${inner}` : pos
  }
  return FIELD_LABELS[key] ?? null
}

/**
 * Aggregates ALL ApiError messages (non_field + field-specific) into a single
 * human-readable string for toasts, field keys spelled the way the shop reads
 * them. For non-ApiError throwables, falls back to .message or to the
 * canonical "unknown" copy. Single source of truth — every mutation hook
 * should pipe its onError → formatApiError(err).
 */
export function formatApiError(err: unknown): string {
  if (isApiError(err)) {
    const parts: string[] = [...err.nonFieldErrors]
    for (const [field, msgs] of Object.entries(err.errors)) {
      const label = fieldLabel(field)
      for (const msg of msgs) parts.push(label ? `${label}: ${msg}` : msg)
    }
    if (parts.length > 0) return parts.join(' • ')
    return defaultMessage(err.status)
  }
  if (err instanceof Error && err.message) return err.message
  return messages.errors.unknown
}
